import dayjs from "dayjs"
import weekOfYear from "dayjs/plugin/weekOfYear"
import isoWeek from "dayjs/plugin/isoWeek"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import isoWeeksInYear from "dayjs/plugin/isoWeeksInYear"
import isLeapYear from "dayjs/plugin/isLeapYear"

const plugin = () => {
  // 加載插件
  dayjs.extend(weekOfYear)
  dayjs.extend(isoWeek)
  dayjs.extend(utc)
  dayjs.extend(timezone)
  dayjs.extend(isoWeeksInYear)
  dayjs.extend(isLeapYear)
}

// API 回傳的 GMT 偏移代表固定時差，必須對應到不實施夏令時間的時區，
// 否則夏令期間會差一小時 (例如 America/Los_Angeles 夏季為 GMT-7)
// 非整點時差沒有對應的 Etc/GMT 時區，改用不實施夏令時間的城市時區
const fractionalOffsetMap: Record<string, string> = {
  "GMT-9:30": "Pacific/Marquesas",
  "GMT+3:30": "Asia/Tehran",
  "GMT+4:30": "Asia/Kabul",
  "GMT+5:30": "Asia/Kolkata",
  "GMT+5:45": "Asia/Kathmandu",
  "GMT+6:30": "Asia/Yangon",
  "GMT+8:45": "Australia/Eucla",
  "GMT+9:30": "Australia/Darwin"
}

// 整點 GMT 偏移轉為固定時差的 Etc/GMT 時區 (範圍 Etc/GMT+12 ~ Etc/GMT-14)
// Etc/GMT 採 POSIX 慣例，正負號與一般標示相反：GMT+8 → Etc/GMT-8
const toEtcTimeZone = (zone: string): string | undefined => {
  const match = /^GMT([+-])(\d{1,2})$/.exec(zone)
  if (!match) return undefined

  const [, sign, hours] = match
  if (Number(hours) === 0) return "Etc/GMT"

  return `Etc/GMT${sign === "+" ? "-" : "+"}${Number(hours)}`
}

// API (initInfo.timeZone) 可能回傳非 IANA 的時區縮寫，需對應到合法的 IANA 時區
const timeZoneAbbreviationMap: Record<string, string> = {
  IST: "Asia/Kolkata"
}

// 將 API 回傳的時區字串解析為合法的 IANA 時區
// 被 setTimezone 使用
const resolveTimeZone = (zone: string): string => {
  if (zone === "") return dayjs.tz.guess()

  return (
    toEtcTimeZone(zone) ||
    fractionalOffsetMap[zone] ||
    timeZoneAbbreviationMap[zone] ||
    zone
  )
}

// 驗證時區字串是否為合法的 IANA 時區
// 被 setTimezone 使用，避免無效時區導致 RangeError (Sentry 9WICKET-CLIENT-28Q)
const isValidTimeZone = (zone: string): boolean => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: zone })
    return true
  } catch {
    return false
  }
}

// 由 useFetchInitInfo 呼叫，設定 dayjs 預設時區
const setTimezone = (zone: string) => {
  const resolved = resolveTimeZone(zone)

  if (isValidTimeZone(resolved)) {
    dayjs.tz.setDefault(resolved)
  } else {
    console.warn(
      `Invalid time zone "${zone}" (resolved: "${resolved}"), falling back to guessed timezone`
    )
    dayjs.tz.setDefault(dayjs.tz.guess())
  }
}

export { setTimezone, resolveTimeZone, plugin }
export default plugin
